/**
 * Explore loading UX:
 *
 * First load (map casino):
 * 1. No geometry yet → large centered spinner, drawer hidden, no map.
 * 2. Geometry arrives → map fades in under the still-visible spinner; drawer stays hidden.
 * 3. EGM data arrives → spinner fades out, drawer appears (header chrome fades in).
 *
 * Timeframe/insight refetch after primed:
 * - Keep map + drawer; hide EGM overlays and drawer header chrome until data arrives.
 *
 * Mapless insight casinos never use the full-screen spinner sequence.
 *
 * Loading skeleton (`skeletonFirstLoad`, on in MapStats): the drawer no longer waits
 * for EGM data on a map casino. As soon as the floor geometry is ready the spinner
 * fades out and the drawer comes up with gray placeholder cards (see exploreSkeleton.ts)
 * that cross-fade to the real cards when the data lands. Off (the default here) keeps
 * the sequence above exactly.
 */

export type ExploreLoadingMode = {
  /** True when the floor plan GeoJSON is ready (not mapless). */
  hasFloorGeometry: boolean;
  /** Large centered spinner overlay until first EGM payload (map casinos). */
  showFullScreenSpinner: boolean;
  /** Mount/fade the floor map under the spinner once geometry exists. */
  showMapUnderSpinner: boolean;
  /** Drawer may render (mapless always; map casinos after first EGM load). */
  showDrawer: boolean;
  /** Hide EGM heat/pins/legend/zoom-pin while data is in flight and map is up. */
  isEgmRefreshing: boolean;
  /** Hide drawer title + insight + timeframe rows (opacity fade). */
  hideDrawerChrome: boolean;
};

export function getExploreLoadingMode(input: {
  isMapless: boolean;
  getGeoJsonLoading: boolean;
  hasGeoJsonData: boolean;
  isSlotPlayDataIsLoading: boolean;
  isCasinoLoading: boolean;
  slotMetaDataLoading: boolean;
  /** True after the first successful EGM load on a map casino (or always for mapless). */
  explorePrimed: boolean;
  /**
   * Bring the drawer (with its loading skeleton) up once the floor geometry is ready
   * instead of holding it behind the full-screen spinner until EGM data arrives.
   * Display-only: no effect on what is fetched or when.
   */
  skeletonFirstLoad?: boolean;
}): ExploreLoadingMode {
  const hasFloorGeometry =
    !input.isMapless && !input.getGeoJsonLoading && input.hasGeoJsonData;

  const dataLoading =
    input.isSlotPlayDataIsLoading || input.isCasinoLoading || input.slotMetaDataLoading;

  // Skeleton first load: the drawer is allowed up as soon as the floor is drawn.
  const skeletonDrawer = !!input.skeletonFirstLoad && hasFloorGeometry;

  // First-load spinner until EGM data has primed Explore (stays up after geometry),
  // or, with the skeleton, only until the floor geometry is ready.
  const showFullScreenSpinner =
    !input.isMapless &&
    !input.explorePrimed &&
    !skeletonDrawer &&
    (dataLoading || input.getGeoJsonLoading);

  const showMapUnderSpinner = showFullScreenSpinner && hasFloorGeometry;

  const showDrawer = input.isMapless || input.explorePrimed || skeletonDrawer;

  // After primed: hide overlays in place. During first-load-with-map: also hide
  // overlays under the spinner so heat/pins don't flash under it.
  const isEgmRefreshing = hasFloorGeometry && dataLoading;

  const hideDrawerChrome = showDrawer && dataLoading;

  return {
    hasFloorGeometry,
    showFullScreenSpinner,
    showMapUnderSpinner,
    showDrawer,
    isEgmRefreshing,
    hideDrawerChrome,
  };
}
